import {useCallback, useEffect, useMemo, useRef} from 'react';
import areEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import {RealtimeMessageType} from 'realtime/Constants';
import decryptRealtimeMessage from 'realtime/decryptRealtimeMessage';
import getRealtimeChannel from 'realtime/getRealtimeChannel';
import RealtimeChannel, {
  EncryptedRealtimeMessage,
  RealtimeChannelCapability,
  RealtimeMessage,
} from 'realtime/RealtimeChannel';
import usePrevious from 'ui-library/hooks/usePrevious';
import {NonEmptyArray} from 'util/Types';

const NO_MESSAGE_FILTER = 'no_filter';
export type MessageFilter = NonEmptyArray<RealtimeMessageType> | typeof NO_MESSAGE_FILTER;

function useRealtimeChannel(
  channelName: string,
  capabilities: Array<RealtimeChannelCapability>
): Promise<RealtimeChannel> | null {
  const previousChannelParams = usePrevious({channelName, capabilities});
  const channelPromiseRef = useRef<Promise<RealtimeChannel> | null>(null);

  // We need to stop any existing channels when the component is unmounted.
  useEffect(
    () => () => {
      channelPromiseRef.current?.then((channel) => {
        channel?.stop();
      });
    },
    []
  );

  const getNewChannel = async (): Promise<RealtimeChannel> => {
    const previousChannel = await channelPromiseRef.current;
    previousChannel?.stop();
    return await getRealtimeChannel(channelName, capabilities);
  };

  if (!areEqual(previousChannelParams, {channelName, capabilities})) {
    channelPromiseRef.current = getNewChannel();
    return channelPromiseRef.current;
  }
  return channelPromiseRef.current;
}

/**
 * React hook which executes a callback whenever a message is posted to the specified channel.
 * @param channelName The name of the channel being subscribed to
 * @param onNewMessage A function to execute when there is a new message on the channel
 * @param messageFilter The message types to support. All messages will be received if null and none if empty.
 */
export default function useRealtimeChannelSubscription<T extends NonEmptyArray<RealtimeMessageType>>(
  channelName: string,
  onNewMessage: (message: Extract<RealtimeMessage, {name: T[number]}>) => void,
  messageFilter: T
): void;
export default function useRealtimeChannelSubscription(
  channelName: string,
  onNewMessage: (message: RealtimeMessage) => void,
  messageFilter: MessageFilter
): void;
export default function useRealtimeChannelSubscription(
  channelName: string,
  onNewMessage: (message: RealtimeMessage) => void,
  messageFilter: MessageFilter
): void {
  const channelPromise = useRealtimeChannel(channelName, [RealtimeChannelCapability.SUBSCRIBE]);

  const messageFilterSet = useMemo(() => {
    if (messageFilter === NO_MESSAGE_FILTER) {
      return NO_MESSAGE_FILTER;
    }
    return new Set<string>(messageFilter);
  }, [messageFilter]);

  const onNewMessageFiltered = useCallback(
    async (message: RealtimeMessage | EncryptedRealtimeMessage) => {
      // don't waste time decrypting a message that will be filtered out
      if (messageFilterSet !== NO_MESSAGE_FILTER && !messageFilterSet.has(message.name)) {
        return;
      }

      try {
        const plainTextMessage = await decryptRealtimeMessage(channelName, message);

        onNewMessage(plainTextMessage);
      } catch (err) {
        assertAndContinue(false, `Error decrypting message: ${message.name}`, err);
        throw err;
      }
    },
    [messageFilterSet, onNewMessage, channelName]
  );

  useEffect(() => {
    channelPromise?.then((channel) => {
      try {
        channel?.subscribe(onNewMessageFiltered);
        // Subscribing to a channel can throw an error if the channel has already been stopped.
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (err) {
        assertAndContinue(
          false,
          `Error subscribing to realtime channel: ${channelName}, ${[RealtimeChannelCapability.SUBSCRIBE]})}`,
          err
        );
      }
    });
    // onNewMessage is a callback, so we don't want to include it in the dependency array since it could get
    // recreated on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, channelPromise]);
}
